import { createApi } from "@reduxjs/toolkit/query/react";

import { REQUEST_METHODS } from "@/constants/global-constants";
import {
  IImportantDocument,
  ISupportTicket,
  ITeamContact,
} from "@/interfaces/support.interface";
import { BaseWithAuth, getCustomerId } from "@/utils/helpers";

export const C_SupportApi = createApi({
  reducerPath: "C_SupportApi",
  baseQuery: BaseWithAuth(),
  tagTypes: ["support", "teamContact", "importantDocument"],
  endpoints: (builder) => ({
    // get all the Customers Support Tickets
    getCustomersSupportTickets: builder.query<
      GeneralResponse<ISupportTicket[] | ISupportTicket>,
      { filters?: string; ticketId?: string }
    >({
      query: ({ filters, ticketId }) => {
        const basePath = `/customer/${getCustomerId()}/support`;
        if (ticketId) {
          return `${basePath}/${ticketId}`;
        }

        return filters ? `${basePath}${filters}` : basePath;
      },
      providesTags: ["support"],
    }),

    //Mutate tickets
    mutateCustomerSupportTicket: builder.mutation<
      object,
      {
        data?: object;
        ticketId?: string;
        method: REQUEST_METHODS;
      }
    >({
      query: ({ data, ticketId, method }) => ({
        url: `customer/${getCustomerId()}/support/${ticketId ? `${ticketId}` : ""}`,
        method,
        body: data,
      }),
      invalidatesTags: ["support"],
    }),
    //Team Contact
    getCustomersTeamContact: builder.query<
      GeneralResponse<ITeamContact[] | ITeamContact>,
      { filters?: string; id?: string }
    >({
      query: ({ filters, id }) => {
        const basePath = `/customer/${getCustomerId()}/support/teams`;
        if (id) {
          return `${basePath}/${id}`;
        }

        return filters ? `${basePath}${filters}` : basePath;
      },
      providesTags: ["teamContact"],
    }),
    //Important Document
    getCustomersImportantDocument: builder.query<
      GeneralResponse<IImportantDocument[] | IImportantDocument>,
      { filters?: string; id?: string }
    >({
      query: ({ filters, id }) => {
        const basePath = `/customer/${getCustomerId()}/support/documents`;
        if (id) {
          return `${basePath}/${id}`;
        }

        return filters ? `${basePath}${filters}` : basePath;
      },
      providesTags: ["importantDocument"],
    }),
    //Add Comment to the ticket
    addCommentAsCustomer: builder.mutation<
      object,
      {
        data?: object;
        ticketId: string;
        method: REQUEST_METHODS;
      }
    >({
      query: ({ data, ticketId, method }) => ({
        url: `customer/${getCustomerId()}/support/${ticketId}/comment`,
        method,
        body: data,
      }),
      invalidatesTags: ["support"],
    }),
  }),
});

export const {
  useGetCustomersSupportTicketsQuery,
  useMutateCustomerSupportTicketMutation,
  useGetCustomersTeamContactQuery,
  useGetCustomersImportantDocumentQuery,
  useAddCommentAsCustomerMutation,
} = C_SupportApi;
